/**
 * WebSocket hook for room connection
 * Connects to the ShowRoom Durable Object for real-time state sync
 */

import { useEffect, useRef, useCallback, useState } from 'react';
import { WS_MESSAGE_TYPES, COMMAND_TYPES } from '@gallery/shared';

export interface RoomState {
  roomCode: string;
  playlistId: string | null;
  itemId: string | null;
  startEpochMs: number;
  offsetMs: number;
  isPaused: boolean;
  controllerClaimed: boolean;
  zones: Record<string, string>;
}

export interface PlaylistItem {
  id: string;
  artworkId: string;
  artworkTitle: string;
  artworkType: 'image' | 'video';
  thumbnailUrl: string | null;
  durationMs: number;
  transition: string;
  zone: string | null;
  orderIndex: number;
}

interface UseRoomWebSocketOptions {
  roomCode: string;
  screenId: string;
  onStateChange?: (state: RoomState) => void;
  onPaired?: (claimed: boolean) => void;
  onError?: (error: string) => void;
}

export function useRoomWebSocket({
  roomCode,
  screenId,
  onStateChange,
  onPaired,
  onError,
}: UseRoomWebSocketOptions) {
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<number | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [roomState, setRoomState] = useState<RoomState | null>(null);

  const connect = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = `${protocol}//${window.location.host}/api/v1/rooms/${roomCode}/ws`;

    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      setIsConnected(true);
      // Send HELLO_SCREEN message
      ws.send(
        JSON.stringify({
          type: WS_MESSAGE_TYPES.HELLO_SCREEN,
          payload: { screenId },
        })
      );
    };

    ws.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);

        switch (message.type) {
          case WS_MESSAGE_TYPES.STATE:
            setRoomState(message.payload);
            onStateChange?.(message.payload);
            break;

          case WS_MESSAGE_TYPES.PAIR_STATUS:
            onPaired?.(message.payload.claimed);
            break;

          case WS_MESSAGE_TYPES.ERROR:
            onError?.(message.payload.message);
            break;

          case WS_MESSAGE_TYPES.DRIFT:
            // Handle drift correction - will be used by the player
            break;
        }
      } catch (err) {
        console.error('Failed to parse WebSocket message:', err);
      }
    };

    ws.onclose = () => {
      setIsConnected(false);
      // Attempt to reconnect after 3 seconds
      reconnectTimeoutRef.current = window.setTimeout(() => {
        connect();
      }, 3000);
    };

    ws.onerror = () => {
      onError?.('WebSocket connection error');
    };
  }, [roomCode, screenId, onStateChange, onPaired, onError]);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
  }, []);

  const sendHeartbeat = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(
        JSON.stringify({
          type: WS_MESSAGE_TYPES.HEARTBEAT,
          payload: { screenId, timestamp: Date.now() },
        })
      );
    }
  }, [screenId]);

  // Connect on mount, disconnect on unmount
  useEffect(() => {
    connect();
    return () => disconnect();
  }, [connect, disconnect]);

  // Send heartbeat every 30 seconds
  useEffect(() => {
    const interval = setInterval(sendHeartbeat, 30000);
    return () => clearInterval(interval);
  }, [sendHeartbeat]);

  return {
    isConnected,
    roomState,
    disconnect,
    sendHeartbeat,
  };
}

/**
 * Hook for controller WebSocket connection
 */
export function useControllerWebSocket({
  roomCode,
  userId,
  onStateChange,
  onPaired,
  onError,
}: {
  roomCode: string;
  userId: string;
  onStateChange?: (state: RoomState) => void;
  onPaired?: (claimed: boolean) => void;
  onError?: (error: string) => void;
}) {
  const wsRef = useRef<WebSocket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isPaired, setIsPaired] = useState(false);
  const [roomState, setRoomState] = useState<RoomState | null>(null);

  const connect = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = `${protocol}//${window.location.host}/api/v1/rooms/${roomCode}/ws`;

    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      setIsConnected(true);
      // Send HELLO_CONTROLLER message
      ws.send(
        JSON.stringify({
          type: WS_MESSAGE_TYPES.HELLO_CONTROLLER,
          payload: { userId },
        })
      );
    };

    ws.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);

        switch (message.type) {
          case WS_MESSAGE_TYPES.STATE:
            setRoomState(message.payload);
            onStateChange?.(message.payload);
            break;

          case WS_MESSAGE_TYPES.PAIR_STATUS:
            setIsPaired(message.payload.claimed);
            onPaired?.(message.payload.claimed);
            break;

          case WS_MESSAGE_TYPES.ERROR:
            onError?.(message.payload.message);
            break;
        }
      } catch (err) {
        console.error('Failed to parse WebSocket message:', err);
      }
    };

    ws.onclose = () => {
      setIsConnected(false);
      setIsPaired(false);
    };

    ws.onerror = () => {
      onError?.('WebSocket connection error');
    };
  }, [roomCode, userId, onStateChange, onPaired, onError]);

  const sendCommand = useCallback(
    (cmdType: keyof typeof COMMAND_TYPES, data?: unknown) => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(
          JSON.stringify({
            type: WS_MESSAGE_TYPES.CMD,
            payload: {
              cmdId: crypto.randomUUID(),
              cmdType,
              data,
            },
          })
        );
      }
    },
    []
  );

  const play = useCallback(() => sendCommand('START'), [sendCommand]);
  const pause = useCallback(() => sendCommand('PAUSE'), [sendCommand]);
  const next = useCallback(() => sendCommand('NEXT'), [sendCommand]);
  const prev = useCallback(() => sendCommand('PREV'), [sendCommand]);
  const setPlaylist = useCallback(
    (playlistId: string) => sendCommand('SET_PLAYLIST', { playlistId }),
    [sendCommand]
  );

  useEffect(() => {
    connect();
    return () => {
      wsRef.current?.close();
    };
  }, [connect]);

  return {
    isConnected,
    isPaired,
    roomState,
    play,
    pause,
    next,
    prev,
    setPlaylist,
    sendCommand,
  };
}
